import { useEffect, useRef } from "react";

interface UseSkipDualFormProps {
    updateFormData: any[];
    flkState: {
        signingUrl: string;
        isSigned: boolean;
    };
    formRef: React.MutableRefObject<any[]>;
}

/**
 * Hook to automatically submit the form if `skipDualForm` is enabled in the configuration.
 * This skips the manual "input" step and goes directly to the FLK signing phase.
 */
const useSkipDualForm = ({ updateFormData, flkState, formRef }: UseSkipDualFormProps) => {
    // Use a ref to track if we've already attempted submission for a specific form index to avoid loops
    // although checking signingUrl === "" should be sufficient, this adds safety.
    const processedRef = useRef<{ [key: number]: boolean }>({});

    useEffect(() => {
        let timer: NodeJS.Timeout;

        // 1. Find the active form
        const activeForm = updateFormData.find((f: any) => f.active);

        if (activeForm && activeForm?.flk_config?.skipDualForm) {
            console.log("test:", activeForm.label, flkState);
            // 2. Check if we need to submit (not signed, no signing url)
            if (
                flkState.signingUrl === "" &&
                !processedRef.current[activeForm.index] // Check if we already processed this form
            ) {
                console.log("Auto-submitting form due to skipDualForm:", activeForm.label);

                // Mark as processed immediately to prevent loops
                processedRef.current[activeForm.index] = true;

                // 3. Trigger submission with a delay to ensure Formik is mounted
                timer = setTimeout(() => {
                    // Try using the formRef first (cleaner)
                    const formInstance = formRef.current[activeForm.index];

                    if (formInstance) {
                        formInstance.submitForm();
                    } else {
                        // Fallback to DOM click if ref is missing for some reason
                        document.getElementById(`submitBtn_${activeForm.index}`)?.click();
                    }
                }, 850);
            }
        }

        return () => {
            if (timer) {
                clearTimeout(timer);
            }
        };
    }, [updateFormData, flkState.signingUrl, formRef]);
};

export default useSkipDualForm;
